/**
 * Cookie Utility Functions
 *
 * SECURITY FIXES:
 * - C-1/H-8: Added 8-hour default expiration for session timeout
 * - C-2: Added Secure and SameSite=Strict flags (HttpOnly not possible client-side)
 * - H-1: Fixed ReDoS vulnerability in getCookie()
 *
 * NOTE: Server SHOULD set cookies with HttpOnly flag via Set-Cookie header.
 * Client-side cookies cannot be HttpOnly and are vulnerable to XSS.
 */

import { addLog } from "./persistentLogger";

const isDev = process.env.NODE_ENV === "development";

/**
 * Set a cookie with security flags and expiration.
 *
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number|null} expiresMs - Expiration in milliseconds (defaults to 8 hours)
 */
export function setCookie(name, value, expiresMs = null) {
  const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;
  const expirationTime = expiresMs !== null ? expiresMs : EIGHT_HOURS_MS;
  const expirationDate = new Date(Date.now() + expirationTime);

  // Convert value to string if it's not already
  const stringValue = String(value);

  // Use SameSite=Lax for local development, Strict for production
  const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const sameSite = isLocalDev ? "Lax" : "Strict";
  const secure = window.location.protocol === "https:";

  // Build cookie string properly without extra spaces
  let cookie = `${name}=${encodeURIComponent(stringValue)}`;
  cookie += `; path=/`;
  cookie += `; expires=${expirationDate.toUTCString()}`;
  cookie += `; max-age=${Math.floor(expirationTime / 1000)}`;
  cookie += `; SameSite=${sameSite}`;

  if (secure) {
    cookie += `; Secure`;
  }

  const logData = {
    cookieString: cookie,
    value: stringValue.substring(0, 20) + "...",
    expiresIn: Math.floor(expirationTime / 1000) + "s",
    sameSite,
    secure,
  };
  addLog("Cookie", `Attempting to set ${name}`, logData);
  if (isDev) console.log(`[Cookie] Attempting to set ${name}:`, logData);

  document.cookie = cookie;

  // Verify cookie was set
  const verifyValue = getCookie(name);
  const verifyData = {
    name,
    wasSet: !!verifyValue,
    allCookies: document.cookie,
  };
  addLog("Cookie", `Verification after set`, verifyData);
  if (isDev) console.log(`[Cookie] Verification after set:`, verifyData);
}

export function eraseCookie(name) {
  if (isDev) console.log(`[Cookie] Erasing ${name}`);
  document.cookie = `${name}=; Max-Age=0; path=/`;
  if (isDev) console.log(`[Cookie] After erase, allCookies:`, document.cookie);
}

/**
 * Get a cookie value by name.
 *
 * SECURITY FIX H-1: Fixed ReDoS vulnerability by using safer parsing.
 * Previous regex was vulnerable to catastrophic backtracking.
 *
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null
 */
export function getCookie(name) {
  // Safer parsing without regex to avoid ReDoS
  const cookies = document.cookie.split("; ");
  for (const cookie of cookies) {
    const [key, value] = cookie.split("=");
    if (key === name) {
      const result = value ? decodeURIComponent(value) : null;
      // Debug logging
      if (process.env.NODE_ENV === "development") {
        console.log(`[Cookie] Get ${name}:`, result ? "FOUND" : "NOT FOUND", {
          allCookies: document.cookie,
        });
      }
      return result;
    }
  }
  // Debug logging
  if (process.env.NODE_ENV === "development") {
    console.log(`[Cookie] Get ${name}: NOT FOUND`, {
      allCookies: document.cookie,
    });
  }
  return null;
}

/**
 * Fallback: set auth cookies from login response ONLY when the server
 * has not already set them via Set-Cookie header (which includes HttpOnly).
 *
 * SECURITY FIX C-1: Cookies now set with 8-hour expiration (default in setCookie).
 * SECURITY FIX C-2: Cookies set with Secure and SameSite=Strict flags.
 * SECURITY FIX H-1: Skip client-side cookie write when the server already set the
 *   cookie (HttpOnly cookies are invisible to JS, so we check for the non-HttpOnly
 *   fallback). Once the backend always sends Set-Cookie with HttpOnly, this
 *   function can be removed entirely.
 *
 * TODO(backend): Migrate login API to return auth tokens via Set-Cookie headers
 *   with HttpOnly; Secure; SameSite=Strict flags. Once confirmed, remove the
 *   client-side setCookie calls below.
 */
export function ensureAuthCookiesFromResponse(response) {
  const callInfo = {
    hasResponse: !!response,
    hasSessionId: !!response?.session_id,
    hasManagerToken: !!response?.manager_token,
    hasSystemId: !!response?.system_id,
  };
  addLog("CookieUtil", "ensureAuthCookiesFromResponse called", callInfo);
  if (isDev) console.log("[CookieUtil] ensureAuthCookiesFromResponse called with:", callInfo);

  if (!response) {
    addLog("CookieUtil", "Response is null/undefined, skipping cookie setup");
    if (isDev) console.warn("[CookieUtil] Response is null/undefined, skipping cookie setup");
    return;
  }

  // Only set client-side if the cookie is not already present
  // (server Set-Cookie with HttpOnly would not be readable here,
  //  but the cookie would already be sent on subsequent requests)
  if (response.session_id) {
    const existingSessionId = getCookie("session_id");
    if (isDev)
      console.log("[CookieUtil] session_id check:", {
        responseHasSessionId: true,
        existingSessionId: existingSessionId ? "EXISTS" : "NOT_EXISTS",
        willSet: !existingSessionId,
      });
    if (!existingSessionId) {
      if (isDev) console.log("[CookieUtil] Setting session_id cookie...");
      setCookie("session_id", response.session_id);
    }
  } else {
    if (isDev) console.warn("[CookieUtil] Response does not have session_id!");
  }

  if (response.manager_token) {
    const existingManagerToken = getCookie("manager_token");
    if (isDev)
      console.log("[CookieUtil] manager_token check:", {
        responseHasManagerToken: true,
        existingManagerToken: existingManagerToken ? "EXISTS" : "NOT_EXISTS",
        willSet: !existingManagerToken,
      });
    if (!existingManagerToken) {
      if (isDev) console.log("[CookieUtil] Setting manager_token cookie...");
      setCookie("manager_token", response.manager_token);
    }
  } else {
    if (isDev) console.warn("[CookieUtil] Response does not have manager_token!");
  }

  if (response.system_id) {
    const existingSystemId = getCookie("system_id");
    if (isDev)
      console.log("[CookieUtil] system_id check:", {
        responseHasSystemId: true,
        existingSystemId: existingSystemId ? "EXISTS" : "NOT_EXISTS",
        willSet: !existingSystemId,
      });
    if (!existingSystemId) {
      if (isDev) console.log("[CookieUtil] Setting system_id cookie...");
      setCookie("system_id", response.system_id);
    }
  } else {
    if (isDev) console.warn("[CookieUtil] Response does not have system_id!");
  }

  if (isDev)
    console.log("[CookieUtil] Final cookie state:", {
      sessionId: getCookie("session_id") ? "SET" : "NOT_SET",
      managerToken: getCookie("manager_token") ? "SET" : "NOT_SET",
      systemId: getCookie("system_id") ? "SET" : "NOT_SET",
      allCookies: document.cookie,
    });
}

export function eraseAuthCookies() {
  eraseCookie("session_id");
  eraseCookie("manager_token");
  eraseCookie("system_id");
}
